/**
 * B2cRescheduleModal Component
 *
 * Rescheduling for B2C website orders from the tracking page (PLA-878..881).
 * One-screen day-column picker -> single confirmation -> success, with a month
 * calendar for later dates and in-place recovery when a chosen slot was taken.
 *
 *   pick --slot--> confirm --ok--> done --Fertig--> closed
 *    ^  \--cal--> calendar --day--/     |
 *    +------Zurück / Andere Uhrzeit-----+ (slot-taken: that time is removed)
 *
 * Slot derivation lives in `b2cReschedule/rescheduleSlotModel` (pure); this
 * component owns the modal frame, view state and the opening-hours fetch.
 *
 * @module components/B2cRescheduleModal
 */

import { FC, useState, useEffect, useMemo, useCallback } from "react"
import {
  Modal,
  View,
  ViewStyle,
  TextStyle,
  TouchableOpacity,
  ScrollView,
  Pressable,
  LayoutChangeEvent,
  useWindowDimensions,
} from "react-native"
import * as Sentry from "@sentry/react"
import dayjs from "dayjs"
import Icon from "react-native-vector-icons/MaterialIcons"

import "dayjs/locale/de"
import { Text } from "@/components/Text"
import { getB2cOpeningHours, type B2cOpeningHoursResponse } from "@/services/firebase/functions"
import { colors } from "@/theme"

import { CalendarView } from "./b2cReschedule/CalendarView"
import { CancelConfirmView, CancelLink } from "./b2cReschedule/CancelConfirmView"
import { ConfirmView } from "./b2cReschedule/ConfirmView"
import { ContextBanner } from "./b2cReschedule/ContextBanner"
import { PickBody } from "./b2cReschedule/PickBody"
import { isRecoveryIntent, type PickerIntent } from "./b2cReschedule/pickerIntent"
import {
  buildCalendarMonth,
  calendarBounds,
  monthStartOf,
  shiftMonth,
} from "./b2cReschedule/rescheduleCalendarModel"
import {
  classifyRescheduleError,
  slotTakenMessage,
  TRANSIENT_RETRY_MESSAGE,
} from "./b2cReschedule/rescheduleErrors"
import {
  buildConfirmData,
  buildContextBanner,
  buildGrid,
  columnCountForWidth,
  currentSlot,
  listOpenDates,
  previousAppointmentLabel,
  visibleDates,
} from "./b2cReschedule/rescheduleSlotModel"
import { SuccessView } from "./b2cReschedule/SuccessView"
import { font, tokens } from "./b2cReschedule/tokens"
import { useRescheduleSlots } from "./b2cReschedule/useRescheduleSlots"

dayjs.locale("de")

type ViewState = "pick" | "calendar" | "confirm" | "done" | "cancelConfirm"

/** Viewport width up to which the modal uses its compact (phone) paddings. */
const COMPACT_MAX_WIDTH = 520

export interface B2cRescheduleModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: (data: { abholdatum: string; spatesteAbholung: string }) => void
  isLoading?: boolean
  errorMessage?: string | null
  /** Callable code of the failed reschedule; tells slot-taken from transient. */
  errorCode?: string | null
  /** Drops the store's reschedule error when the customer goes back to the grid. */
  onClearError?: () => void
  /** True once the store confirmed a successful reschedule; shows the success screen. */
  succeeded?: boolean
  /** Slot the store confirmed for this session; the success screen shows it. */
  confirmedSlot?: { abholdatum: string; spatesteAbholung: string } | null
  /** "cancel" = opened from "Stornieren": offers rebooking first, cancel as quiet fallback. */
  intent?: PickerIntent
  onConfirmCancel?: () => void
  isCancelling?: boolean
  cancelErrorMessage?: string | null
  locationName?: string
  /** Prüfstelle phone line; hidden unless provided (no data source exists yet). */
  phone?: string | null
  phoneHours?: string | null
  trackingToken: string
  initialDate?: string // ISO string
  initialTime?: string // HH:MM
  serviceType?: "pickup_delivery" | "driveby"
}

const HEADER_TITLES: Record<ViewState, string> = {
  pick: "Neuen Termin wählen",
  calendar: "Datum wählen",
  confirm: "Termin bestätigen",
  done: "Termin gebucht",
  cancelConfirm: "Termin stornieren",
}

export const B2cRescheduleModal: FC<B2cRescheduleModalProps> = ({
  visible,
  onClose,
  onSubmit,
  isLoading = false,
  errorMessage = null,
  errorCode = null,
  onClearError,
  succeeded = false,
  confirmedSlot = null,
  intent = "reschedule",
  onConfirmCancel,
  isCancelling = false,
  cancelErrorMessage = null,
  locationName,
  phone,
  phoneHours,
  trackingToken,
  initialDate,
  initialTime,
  serviceType = "pickup_delivery",
}) => {
  const [view, setView] = useState<ViewState>("pick")
  const [selection, setSelection] = useState<{ date: string; time: string } | null>(null)
  const [pageStart, setPageStart] = useState(0)
  const [expanded, setExpanded] = useState(false)
  const [taken, setTaken] = useState<Record<string, string[]>>({})
  const [calendarMonth, setCalendarMonth] = useState(() => monthStartOf(dayjs()))
  const [contentWidth, setContentWidth] = useState<number | null>(null)
  const [openingHours, setOpeningHours] = useState<B2cOpeningHoursResponse | null>(null)
  const [loadingHours, setLoadingHours] = useState(false)
  const [hoursFailed, setHoursFailed] = useState(false)
  const [now, setNow] = useState(() => dayjs())
  const { width: windowWidth } = useWindowDimensions()
  const compact = windowWidth <= COMPACT_MAX_WIDTH

  const location = useMemo(() => locationName || "Nürnberg", [locationName])
  const hours = openingHours?.openingHours ?? null
  const columnCount = columnCountForWidth(contentWidth)

  // Reset on open
  useEffect(() => {
    if (!visible) return
    setView("pick")
    setSelection(null)
    setPageStart(0)
    setExpanded(false)
    setTaken({})
    setOpeningHours(null)
    setHoursFailed(false)
    setNow(dayjs())
  }, [visible])

  // Fetch opening hours (token-scoped)
  useEffect(() => {
    if (!visible) return
    let cancelled = false
    setLoadingHours(true)
    getB2cOpeningHours(location, trackingToken)
      .then((res) => {
        if (!cancelled) setOpeningHours(res)
      })
      .catch((error) => {
        if (cancelled) return
        setHoursFailed(true)
        // Terminal: the customer has no days to pick from.
        Sentry.captureException(error, {
          tags: { flow: "reschedule", operation: "fetch-opening-hours" },
        })
      })
      .finally(() => {
        if (!cancelled) setLoadingHours(false)
      })
    return () => {
      cancelled = true
    }
  }, [visible, location, trackingToken])

  const openDates = useMemo(() => listOpenDates(hours, now), [hours, now])
  const pageDates = useMemo(
    () => visibleDates(openDates, pageStart, columnCount),
    [openDates, pageStart, columnCount],
  )
  const { results, refetchDay } = useRescheduleSlots({
    active: visible,
    location,
    serviceType,
    hours,
    dates: pageDates,
  })
  // Move intent: the current appointment shows as a disabled "Ihr Termin" slot.
  const mine = useMemo(
    () => (intent === "reschedule" ? currentSlot(initialDate, initialTime) : null),
    [intent, initialDate, initialTime],
  )
  const grid = useMemo(
    () => buildGrid({ openDates, results, pageStart, columnCount, now, taken, mine }),
    [openDates, results, pageStart, columnCount, now, taken, mine],
  )
  const calendar = useMemo(
    () => buildCalendarMonth({ month: calendarMonth, openDates, results, taken, now }),
    [calendarMonth, openDates, results, taken, now],
  )

  // The success screen shows what the store confirmed for THIS session, never the
  // modal's current selection (a stale completion must not relabel a new pick).
  const shownSlot = useMemo(
    () =>
      succeeded && confirmedSlot
        ? { date: confirmedSlot.abholdatum, time: confirmedSlot.spatesteAbholung }
        : selection,
    [succeeded, confirmedSlot, selection],
  )
  const effectiveView: ViewState = succeeded && shownSlot ? "done" : view
  // Dismissal is a no-op while a mutation is in flight.
  const requestClose = () => {
    if (isLoading || isCancelling) return
    onClose()
  }
  const confirmData = useMemo(
    () =>
      shownSlot
        ? buildConfirmData(
            shownSlot.date,
            shownSlot.time,
            serviceType,
            initialDate,
            initialTime,
            intent,
          )
        : null,
    [shownSlot, serviceType, initialDate, initialTime, intent],
  )
  const banner = useMemo(
    () => buildContextBanner(intent, initialDate, initialTime),
    [intent, initialDate, initialTime],
  )

  // ADR-0036: slot-taken / expected / transient are breadcrumbs; only a defect is captured.
  const errorKind = errorMessage ? classifyRescheduleError(errorCode, errorMessage) : null
  useEffect(() => {
    if (!errorMessage) return
    const kind = classifyRescheduleError(errorCode, errorMessage)
    if (kind === "unexpected") {
      Sentry.captureException(new Error(errorMessage), {
        tags: { flow: "reschedule", operation: "submit", code: errorCode ?? "none" },
      })
      return
    }
    Sentry.addBreadcrumb({
      category: "reschedule",
      level: "warning",
      message: `reschedule submit rejected (${kind})`,
      data: { code: errorCode },
    })
  }, [errorMessage, errorCode])

  const confirmError = useMemo(() => {
    if (!errorMessage) return null
    if (errorKind === "slot-taken" && selection) return slotTakenMessage(selection.time)
    if (errorKind === "transient") return TRANSIENT_RETRY_MESSAGE
    return errorMessage
  }, [errorMessage, errorKind, selection])

  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setContentWidth(e.nativeEvent.layout.width)
  }, [])

  const page = (dir: -1 | 1) =>
    setPageStart((p) => Math.max(0, Math.min(p + dir * columnCount, openDates.length - 1)))

  const goPick = () => {
    onClearError?.()
    setView("pick")
  }

  const handleSelect = (date: string, time: string) => {
    onClearError?.()
    setSelection({ date, time })
    setView("confirm")
  }

  const handleSubmit = () => {
    if (!selection || isLoading) return
    onSubmit({ abholdatum: selection.date, spatesteAbholung: selection.time })
  }

  // Slot-taken recovery: drop that time, refetch only that day, keep the page.
  const handlePickAnother = () => {
    if (selection) {
      const { date, time } = selection
      setTaken((t) => ({ ...t, [date]: [...(t[date] ?? []), time] }))
      refetchDay(date)
    }
    setSelection(null)
    goPick()
  }

  // After a slot-taken rejection every way back drops that time and refetches its day.
  const leaveConfirm = errorKind === "slot-taken" ? handlePickAnother : goPick

  const openCalendar = () => {
    setCalendarMonth(monthStartOf(pageDates[0] ?? now))
    setView("calendar")
  }

  const handleCalendarPick = (date: string) => {
    const index = openDates.indexOf(date)
    if (index < 0) return
    setPageStart(index)
    setExpanded(false)
    setView("pick")
  }

  const bounds = calendarBounds(openDates, now)
  const title =
    effectiveView === "pick" && intent === "cancel"
      ? "Termin verschieben statt stornieren?"
      : effectiveView === "pick" && intent === "reschedule"
        ? "Termin verschieben"
        : HEADER_TITLES[effectiveView]
  const showBack =
    effectiveView === "confirm" || effectiveView === "cancelConfirm" || effectiveView === "calendar"

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={requestClose}>
      <Pressable style={[$modalOverlay, compact && $overlayCompact]} onPress={requestClose}>
        <Pressable style={$modalContainer} onPress={(e) => e.stopPropagation()}>
          <View style={[$header, compact && $headerCompact]}>
            {showBack && (
              <TouchableOpacity
                onPress={effectiveView === "confirm" ? leaveConfirm : goPick}
                activeOpacity={0.7}
                accessibilityLabel="Zurück zur Auswahl"
                style={$backButton}
                disabled={isLoading || isCancelling}
              >
                <Icon name="arrow-back" size={22} color={colors.text} />
              </TouchableOpacity>
            )}
            <Text style={$headerTitle}>{title}</Text>
            <TouchableOpacity
              onPress={requestClose}
              style={$closeButton}
              accessibilityLabel="Schließen"
            >
              <Icon name="close" size={22} color={colors.text} />
            </TouchableOpacity>
          </View>

          <ScrollView
            contentContainerStyle={[$content, compact && $contentCompact]}
            showsVerticalScrollIndicator={false}
          >
            <View onLayout={onLayout} style={$stack}>
              {effectiveView === "pick" && (
                <>
                  {banner && <ContextBanner {...banner} />}
                  <PickBody
                    loading={loadingHours}
                    failed={hoursFailed}
                    noDays={!!hours && openDates.length === 0}
                    view={grid}
                    expanded={expanded}
                    onToggleExpanded={() => setExpanded((e) => !e)}
                    onPrev={() => page(-1)}
                    onNext={() => page(1)}
                    onSelect={handleSelect}
                    onRetryDay={refetchDay}
                    onOpenCalendar={openCalendar}
                    phone={phone}
                    hours={phoneHours}
                  />
                  {/* Outside PickBody: cancelling must stay reachable while availability is
                      loading, failed, or empty (the only exit when rebooking is impossible). */}
                  {intent === "cancel" && <CancelLink onPress={() => setView("cancelConfirm")} />}
                </>
              )}
              {effectiveView === "calendar" && (
                <CalendarView
                  view={calendar}
                  onPrevMonth={() => setCalendarMonth((m) => shiftMonth(m, -1, bounds))}
                  onNextMonth={() => setCalendarMonth((m) => shiftMonth(m, 1, bounds))}
                  onPick={handleCalendarPick}
                />
              )}
              {effectiveView === "cancelConfirm" && (
                <CancelConfirmView
                  appointment={
                    previousAppointmentLabel(initialDate, initialTime)?.replace("Bisher: ", "") ??
                    null
                  }
                  isLoading={isCancelling}
                  errorMessage={cancelErrorMessage}
                  onBack={goPick}
                  onConfirm={() => onConfirmCancel?.()}
                />
              )}
              {effectiveView === "confirm" && confirmData && (
                <ConfirmView
                  data={confirmData}
                  confirmLabel={isRecoveryIntent(intent) ? "Neuen Termin bestätigen" : undefined}
                  locationName={locationName}
                  isLoading={isLoading}
                  errorMessage={confirmError}
                  slotTaken={errorKind === "slot-taken"}
                  onBack={leaveConfirm}
                  onConfirm={handleSubmit}
                  onPickAnother={handlePickAnother}
                />
              )}
              {effectiveView === "done" && confirmData && (
                <SuccessView data={confirmData} onDone={onClose} />
              )}
            </View>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  )
}

// ─── Styles ─────────────────────────────────────────────────────────

const $modalOverlay: ViewStyle = {
  flex: 1,
  backgroundColor: colors.overlay || "rgba(17, 30, 44, 0.5)",
  justifyContent: "center",
  alignItems: "center",
  padding: 32,
}
const $overlayCompact: ViewStyle = { padding: 16 }

const $modalContainer: ViewStyle = {
  backgroundColor: colors.surface,
  borderRadius: 12,
  width: "100%",
  maxWidth: 500,
  maxHeight: "100%",
  // @ts-ignore - Web-specific
  boxShadow: "0 8px 24px rgba(17, 30, 44, 0.25)",
  overflow: "hidden",
}

const $header: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
  paddingTop: 18,
  paddingBottom: 18,
  paddingRight: 20,
  paddingLeft: 28,
  borderBottomWidth: 1,
  borderBottomColor: tokens.modalBorder,
}
const $headerCompact: ViewStyle = { paddingVertical: 14, paddingRight: 12, paddingLeft: 20 }

const $headerTitle: TextStyle = {
  fontSize: 18,
  fontFamily: font.bold,
  color: colors.text,
  flex: 1,
  minWidth: 0,
}

const $backButton: ViewStyle = { padding: 7, marginLeft: -10, borderRadius: 8 }
const $closeButton: ViewStyle = {
  padding: 7,
  borderRadius: 8,
  cursor: "pointer" as any,
}

const $content: ViewStyle = { paddingTop: 20, paddingBottom: 24, paddingHorizontal: 28 }
const $contentCompact: ViewStyle = { paddingTop: 16, paddingBottom: 20, paddingHorizontal: 20 }
const $stack: ViewStyle = { gap: 16 }
